import * as XLSX from 'xlsx/xlsx.mjs';
import * as moment from 'moment-timezone';

export function pickerDate(value) {
  if (!value) return '';
  if (value instanceof Date) return value;
  const formats = [
    'll', 'LL',
    'MMM D, YYYY', 'MMMM D, YYYY',
    'M/D/YYYY', 'MM/DD/YYYY',
    'M-D-YYYY', 'MM-DD-YYYY',
    'YYYY-MM-DD', 'YYYY/MM/DD',
    'D MMM YYYY', 'D MMMM YYYY',
    'D/M/YYYY', 'DD/MM/YYYY',
  ];
  const strict = moment(value, formats, true);
  if (strict.isValid()) return strict.toDate();
  // Anything else — a full timestamp, say — still opens the picker on its day.
  const lenient = moment(value);
  return lenient.isValid() ? lenient.toDate() : '';
}
export function checkEmail(email) {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
}
export function checkPhoneFormat(number){
  const phoneRegex = /^\d{3} \d{3} \d{4}$/;
  return phoneRegex.test(number)
}
export function checkUrlFormat(url) {
  var expression = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gi;
  return expression.test(url);
}
export function areAllDigitsSame(number) {
  const digits = String(number).split("").map(Number);
  const firstDigit = digits[0];

  for (let i = 1; i < digits.length; i++) {
    if (digits[i] !== firstDigit) {
      return false;
    }
  }

  return true;
}
export function exportToExcel(data, fileName) {
  const workbook = XLSX.utils.book_new();

  // Convert the array of objects to a worksheet
  const worksheet = XLSX.utils.json_to_sheet(data);

  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

  // Write the workbook to a file
  XLSX.writeFile(workbook, fileName + '.xlsx');
}

export function checkValueExists(value){
  if(value) return value
  else return null
}

export function checkDescriptionLength(description, maxLength = 100) {
  return description.length >= maxLength;
}

// Reads the extension off a stored document link. Signed URLs carry query params, and the
// bucket path itself may contain dots, so only the last path segment is inspected.
export function getFileExtension(link) {
  const fileName = String(link || "").split("?")[0].split("/").pop().toLowerCase();
  return fileName.includes(".") ? fileName.split(".").pop() : "";
}

// Resolves how a stored document should be previewed. Anything that is not a known image
// is rendered in the iframe viewer, which handles PDFs and extension-less files.
export function getMediaType(link) {
  const imageTypes = ["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg", "heic", "heif"];
  return imageTypes.includes(getFileExtension(link)) ? "image" : "pdf";
}

// Files stored without an extension are served by S3 as binary/octet-stream, which the
// browser downloads instead of rendering. Sniffing the magic bytes lets us re-wrap the
// response in a blob with the real type so it previews inline.
export function detectMimeFromBytes(buffer) {
  const bytes = new Uint8Array(buffer.slice(0, 12));
  const startsWith = (signature) => signature.every((byte, index) => bytes[index] === byte);
  if (startsWith([0x25, 0x50, 0x44, 0x46])) return "application/pdf";
  if (startsWith([0x89, 0x50, 0x4e, 0x47])) return "image/png";
  if (startsWith([0xff, 0xd8, 0xff])) return "image/jpeg";
  if (startsWith([0x47, 0x49, 0x46, 0x38])) return "image/gif";
  if (startsWith([0x42, 0x4d])) return "image/bmp";
  // RIFF container: bytes 8-11 spell WEBP for images
  if (startsWith([0x52, 0x49, 0x46, 0x46]) &&
    [0x57, 0x45, 0x42, 0x50].every((byte, index) => bytes[8 + index] === byte)) {
    return "image/webp";
  }
  return "";
}

const PENDING_REDIRECT_KEY = "pendingRedirect";
const PENDING_REDIRECT_MAX_AGE = 30 * 60 * 1000;

export function savePendingRedirect(path) {
  try {
    if (!path) return;
    localStorage.setItem(PENDING_REDIRECT_KEY, JSON.stringify({ path, at: Date.now() }));
  } catch (error) {
    // A browser that refuses storage simply does not resume the request.
  }
}

export function takePendingRedirect() {
  try {
    const raw = localStorage.getItem(PENDING_REDIRECT_KEY);
    if (!raw) return null;
    localStorage.removeItem(PENDING_REDIRECT_KEY);
    const saved = JSON.parse(raw);
    if (!saved || !saved.path) return null;
    if (Date.now() - (saved.at || 0) > PENDING_REDIRECT_MAX_AGE) return null;
    return saved.path;
  } catch (error) {
    return null;
  }
}

export function hasPendingRedirect() {
  try {
    return !!localStorage.getItem(PENDING_REDIRECT_KEY);
  } catch (error) {
    return false;
  }
}

// The open table view lives in ?view=<id>, so a reload or a shared link
// reopens it. Default views have no id and leave the param off.
function isCustomTableView(view) {
  return !!view && view.id != null && view.is_default !== true;
}

// Index of the view the url asks for, or -1 when there is none (or it is gone).
export function viewIndexFromQuery(views, viewId) {
  if (!viewId) return -1;
  return views.findIndex((view) => isCustomTableView(view) && `${view.id}` === `${viewId}`);
}

// Swaps the address bar's query for `query`. history.replaceState rather than
// $router.replace: the website's router-view is keyed on $route.fullPath, so a
// router navigation would remount the whole page just to change a tab.
// $route keeps the query the page was opened with.
export function replaceUrlQuery(router, route, query) {
  const { href } = router.resolve({ name: route.name, params: route.params, query });
  window.history.replaceState(window.history.state, "", href);
}

// Writes the view into the address bar.
export function setViewInUrl(router, route, view) {
  const query = { ...route.query };
  delete query.view;
  if (isCustomTableView(view)) query.view = `${view.id}`;
  replaceUrlQuery(router, route, query);
}

// Google Ads: the registration conversion. The tag is loaded only on the
// register page (not app-wide); loading it there also lets it pick the ad
// click's gclid up off the register url, which is what ties the conversion to
// the click.
const GOOGLE_ADS_ID = "AW-11418292853";
const REGISTRATION_CONVERSION = `${GOOGLE_ADS_ID}/BWqiCOmSlpYdEPWk1cQq`;

export function loadGoogleAdsTag() {
  if (window.gtag) return; // already loaded
  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
  document.head.appendChild(script);
  window.dataLayer = window.dataLayer || [];
  // gtag must push the `arguments` object itself, not an array copy of it.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
  window.gtag("config", GOOGLE_ADS_ID);
}

// Fires the conversion once per company, and resolves when Google has it — the
// caller redirects straight to Stripe after, which would otherwise cancel the
// request. Never waits more than 1.5s, and never throws: a blocked tag must
// not hold up a sign-up.
export function trackRegistrationConversion(companyId) {
  return new Promise((resolve) => {
    const sentKey = `dpb_registration_conversion:${companyId}`;
    try {
      if (!window.gtag || sessionStorage.getItem(sentKey)) return resolve();
      sessionStorage.setItem(sentKey, "1");
    } catch (e) {
      if (!window.gtag) return resolve();
    }
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      resolve();
    };
    setTimeout(finish, 1500);
    window.gtag("event", "conversion", {
      send_to: REGISTRATION_CONVERSION,
      event_callback: finish,
    });
  });
}
